import axios from 'lib/apiClient';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import isObject from 'lodash/isObject';
import sortBy from 'lodash/sortBy';
import { BUCKET_URL } from '../constants';
import {
  getContact,
  contactActions as getContactActions,
} from './contacts.service';
import { fileToObject } from './files.service';
import { uploadMedia } from './media';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export async function tryFetchNewActionForMessage(contactId, msgId) {
  let tryCount = 0;
  let found = false;
  let interval = 1000;

  while (tryCount < 5 && !found) {
    try {
      const [action] = await getContactActions(null, {
        action: 'receipt.outgoing',
        contact_id: contactId,
        limit: 1,
      });
      if (action && action.contact_action_data.message.msg_id === msgId) {
        found = true;
        return action;
      }
    } catch (e) {
      console.error(e);
    }
    tryCount++;
    await sleep(interval);
    interval *= 1.5;
  }
}

export const createConversation = async (contactId, accountId) => {
  const convo = await getInboxItems({
    contact_id: contactId,
    account_id: accountId,
  });
  if (!convo.length) {
    return axios.post('API/1_0/conversation.php', {
      contact_id: contactId,
      account_id: accountId,
    });
  }
  return convo[0];
};

export const getPendingMessages = (query) => {
  if (query.conn_id === 'all') {
    delete query.conn_id;
  }
  return axios.get('API/1_0/message.php', {
    params: { ...query, msg_sent: 0, load_groups: 1 },
  });
};

export const getInboxItems = (query) => {
  if (query.conn_id === 'all') {
    delete query.conn_id;
  }
  query.load_contact = 1;
  return axios.get('API/1_0/inboxItems.php', { params: query });
};

export const updateInboxItem = (hash, payload) =>
  axios.post('API/1_0/inboxItems.php', { hash, ...payload, load_contact: 1 });

export const updateConversation = (hash, update) =>
  axios.post('API/1_0/conversation.php', { ...update, hash });

export const getMessages = async (convoHash, query) => {
  query.hash = convoHash;
  if (query.conn_id === 'all') {
    delete query.conn_id;
  }
  const convo = await axios
    .get('API/1_0/conversation.php', { params: query })
    .then((convo) => {
      return convo[0];
    });

  if (!convo) {
    throw new Error('Conversation not found.');
  }
  const messagesAndHistory = [];
  convo.history.forEach((i) => {
    // history_obj is empty string in some cases
    // TODO history_category is missing
    if (isObject(i.history_obj)) {
      if (
        ['inbound_message', 'outbound_message'].includes(i.history_category)
      ) {
        messagesAndHistory.push({
          ...i.history_obj,
          campaign_step_hash: i.campaign_step_hash,
          msg_media_url: i.history_obj.msg_media_hash,
          hash: i.history_hash,
          date: i.history_date,
          msg_stamp: i.history_date,
          msg_type: i.history_type,
          msg_incoming: i.history_category === 'inbound_message' ? '1' : '0',
          workflow_scheduled_step_hash: i.workflow_scheduled_step_hash,
          sender: i.sender_profile,
          file: i.file,
        });
      } else if (
        ['joined_group', 'opted_out', 'left_group'].includes(i.history_type)
      ) {
        i.history_date &&
          messagesAndHistory.push({
            ...i,
            date: i.history_date,
            type: 'history',
            msg_content: i.history_obj.msg_content.replace('Contact', ''),
          });
      } else if (['voice_mails', 'calls'].includes(i.history_category)) {
        messagesAndHistory.push({
          ...i,
          date: i.history_date,
          type: i.history_category,
          sender: i.sender_profile,
        });
      } else if (i.history_category === 'user_note') {
        messagesAndHistory.push({
          ...i,
          date: i.history_date,
          type: 'user_note',
          msg_content: i.history_obj.user_note,
          sender: i.sender_profile,
        });
      }
    }
  });
  convo.messages = sortBy(messagesAndHistory, 'date');
  return convo;
};

export const getSentMessages = (query) => {
  if (query.conn_id === 'all') {
    delete query.conn_id;
  }
  return axios.get('API/1_0/message.php', {
    params: {
      msg_sent: 1,
      load_groups: 1,
      ...query,
    },
  });
};

export const deleteMessage = (hash) =>
  axios.delete('API/1_0/message.php', { params: { hash } });

export const setArchiveConversations = (ids, status) => {
  return axios.post('API/1_0/conversation.php', {
    conv_archived: status,
    hash: ids,
  });
};

export const updateMultiConversations = (ids, payload) => {
  return axios.post('API/1_0/conversation.php', {
    ...payload,
    hash: ids,
  });
};

export const setArchiveAllConversations = (status) => {
  return axios.post('API/1_0/postInboxItemUpdateAll.php', {
    inbox_item_archived: status,
  });
};

export const getMediaUrl = (link) => {
  return new Promise((resolve) => resolve(BUCKET_URL + link));
  // return axios
  //   .get('API/1_0/getMediaURL.php', { params: { media_hash: link } })
  //   .then((r) => r.media_url);
};

export const getNewMessagesCount = () => {
  return axios
    .get('API/1_0/conversation.php', {
      params: { conv_active: 1, load_contacts: 1 },
    })
    .then((r) => {
      const grouped = groupBy(r, 'conv_archived');
      return {
        inbox: get(grouped, '0', []).length,
        archived: get(grouped, '1', []).length,
      };
    });
};

export const getMessageStatus = (hash) => {
  return axios.get('API/1_0/getMessageStatus.php', { params: { hash } });
};

export const sendMessageReply = async (
  contactId,
  { message, file, attachments, msg_type = 'sms', ...rest },
) => {
  const payload = {
    msg_type,
    recipient_type: 'contact',
    recipient_id: contactId,
    msg_content: message,
    ...rest,
  };
  if (file) {
    const { file_id } = await uploadMedia(file);
    payload.file_id = file_id;
  }
  const resp = await axios.post(`API/1_0/message.php`, payload);
  if (attachments) {
    await Promise.allSettled(
      attachments.map(async (attachment) => {
        return fileToObject('message', resp.msg_id, attachment.file_id);
      }),
    );
  }
  return resp;
};

export const getInboxes = () => axios.get('API/1_0/getInboxList.php');

export const contactActions = async (contact_hash, query = {}) => {
  const contact = await getContact({ hash: contact_hash });
  if (!contact) {
    throw new Error('Contact not found');
  }
  if (query.inbox_id === 'all') {
    delete query.inbox_id;
  }
  const actions = await getContactActions(contact_hash, query);
  contact.messages = actions.reverse();
  return contact;
};
